import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';
import {
  getErrorMessageFromResponse,
  throwIfHttpError
} from './httpErrorMessage';
import { BASE_URL } from './constants';

/** Geographic regions for target persona lead search (person location). */
export type TargetPersonaTargetLocation = 'APAC' | 'AMER' | 'EMEA';

/** Target persona configuration for a campaign (UI model). */
export interface TargetPersonaConfig {
  /** Description of who you're selling to (e.g. "B2B SaaS companies with 50-500 employees") */
  targetAudience: string;
  /** Target job titles (e.g. ["VP Sales", "CEO", "Head of Marketing"]) */
  targetTitles: string[];
  /** Max leads to search per account. Higher cap consumes more credits. */
  maxLeadsPerAccount: number;
  /** Regions for lead location matching. Empty means none selected (save before search). */
  targetLocations: TargetPersonaTargetLocation[];
}

/** PUT request body - matches backend API. */
interface PutTargetPersonaRequest {
  target_titles: string[];
  persona_description: string;
  max_leads: number;
  target_locations: TargetPersonaTargetLocation[];
}

/** Backend GET response - CampaignTargetPersonaResponse. */
interface TargetPersonaApiResponse {
  target_titles: string[];
  persona_description: string | null;
  max_leads: number | null;
  target_locations: TargetPersonaTargetLocation[] | null;
}

/** GET/PUT response wraps target_persona_config. */
interface GetTargetPersonaApiResponse {
  target_persona_config: TargetPersonaApiResponse | null;
}

const TARGET_PERSONA_BASE = `${BASE_URL}/v1/campaigns`;

/** All supported region values (for multi-select UI). */
export const TARGET_PERSONA_LOCATION_OPTIONS: readonly TargetPersonaTargetLocation[] =
  ['APAC', 'AMER', 'EMEA'];

function isTargetLocation(s: string): s is TargetPersonaTargetLocation {
  return (TARGET_PERSONA_LOCATION_OPTIONS as readonly string[]).includes(s);
}

function normalizeTargetLocationsFromApi(
  raw: TargetPersonaApiResponse['target_locations']
): TargetPersonaTargetLocation[] {
  if (raw === null || raw === undefined) {
    return [...TARGET_PERSONA_LOCATION_OPTIONS];
  }
  if (!Array.isArray(raw)) {
    return [...TARGET_PERSONA_LOCATION_OPTIONS];
  }
  const filtered = raw.filter(
    (x): x is TargetPersonaTargetLocation =>
      typeof x === 'string' && isTargetLocation(x)
  );
  return filtered;
}

export function defaultTargetPersonaConfig(): TargetPersonaConfig {
  return {
    targetAudience: '',
    targetTitles: [],
    maxLeadsPerAccount: 10,
    targetLocations: [...TARGET_PERSONA_LOCATION_OPTIONS]
  };
}

function apiResponseToConfig(
  res: TargetPersonaApiResponse | null
): TargetPersonaConfig {
  if (!res) return defaultTargetPersonaConfig();
  return {
    targetAudience: res.persona_description ?? '',
    targetTitles: Array.isArray(res.target_titles) ? res.target_titles : [],
    maxLeadsPerAccount:
      typeof res.max_leads === 'number' && res.max_leads >= 1
        ? Math.min(500, Math.floor(res.max_leads))
        : 10,
    targetLocations: normalizeTargetLocationsFromApi(res.target_locations)
  };
}

/** Fetch target persona from backend. */
export async function getCampaignTargetPersona(
  campaignId: string
): Promise<TargetPersonaConfig> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${TARGET_PERSONA_BASE}/${campaignId}/target-persona-config`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      }
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  const data: GetTargetPersonaApiResponse = await response.json();
  return apiResponseToConfig(data.target_persona_config);
}

/** Save target persona to backend. */
export async function putCampaignTargetPersona(
  campaignId: string,
  config: TargetPersonaConfig
): Promise<TargetPersonaConfig> {
  const userSessionToken = getUserSessionCookie();
  const body: PutTargetPersonaRequest = {
    target_titles: config.targetTitles
      .map((t) => t.trim())
      .filter((t) => t !== ''),
    persona_description: config.targetAudience.trim(),
    max_leads: Math.max(
      1,
      Math.min(500, Math.floor(config.maxLeadsPerAccount) || 10)
    ),
    target_locations: [...config.targetLocations]
  };
  const response = await apiFetch(
    `${TARGET_PERSONA_BASE}/${campaignId}/target-persona-config`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify(body)
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  const text = await response.text();
  if (!text) return config;
  try {
    const data: GetTargetPersonaApiResponse = JSON.parse(text);
    return apiResponseToConfig(data.target_persona_config);
  } catch {
    return config;
  }
}

/** Request body for POST target-persona/execute. */
export interface StartTargetPersonaFetchRequest {
  row_ids: number[];
}

/** Response from POST target-persona/execute. */
export interface StartTargetPersonaFetchResponse {
  workflow_id: string;
}

/** Get the active (RUNNING) campaign target persona fetch workflow ID. Returns null if 404 (no active workflow). */
export async function getActiveTargetPersonaFetchWorkflowId(
  campaignId: string
): Promise<{ workflow_id: string } | null> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${TARGET_PERSONA_BASE}/${campaignId}/target-persona-config/execute/active-workflow-id`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      }
    }
  );
  if (response.status === 404) {
    return null;
  }
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  return response.json();
}

/** Start target persona fetch for given row IDs. Returns workflow ID for SSE streaming. */
export async function startTargetPersonaFetch(
  campaignId: string,
  request: StartTargetPersonaFetchRequest
): Promise<StartTargetPersonaFetchResponse> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${TARGET_PERSONA_BASE}/${campaignId}/target-persona-config/execute`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify(request)
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  return response.json();
}

/** Status event from target persona fetch SSE stream. */
export type TargetPersonaFetchStatus = 'RUNNING' | 'COMPLETED' | 'FAILED';

export interface TargetPersonaFetchStreamEvent {
  status?: TargetPersonaFetchStatus;
  error?: string;
}

/** Callbacks for target persona fetch SSE stream. */
export interface TargetPersonaFetchStreamCallbacks {
  onStatus: (status: TargetPersonaFetchStatus, error?: string) => void;
  onComplete: () => void;
  onError: (message: string) => void;
}

/** Lead returned by target-leads API. */
export interface CampaignTargetLead {
  name: string | null;
  linkedin_url: string;
  title: string | null;
}

/** Response from GET target-leads. */
interface GetTargetLeadsApiResponse {
  leads: CampaignTargetLead[];
}

/** Fetch campaign target leads for a company. Returns leads from campaign_account_target_persona enriched with profile data. */
export async function getCampaignTargetLeads(
  campaignId: string,
  companyUrl: string
): Promise<CampaignTargetLead[]> {
  const userSessionToken = getUserSessionCookie();
  const search = new URLSearchParams();
  search.set('company_url', companyUrl.trim());
  const url = `${TARGET_PERSONA_BASE}/${campaignId}/target-leads?${search.toString()}`;
  const response = await apiFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${userSessionToken}`
    }
  });
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  const data: GetTargetLeadsApiResponse = await response.json();
  const raw = Array.isArray(data?.leads) ? data.leads : [];
  return raw.filter(
    (l): l is CampaignTargetLead => l != null && typeof l === 'object'
  );
}

/** Subscribe to SSE stream of target persona fetch status. */
export function streamTargetPersonaFetchResult(
  campaignId: string,
  workflowId: string,
  callbacks: TargetPersonaFetchStreamCallbacks
): () => void {
  const userSessionToken = getUserSessionCookie();
  const encodedWorkflowId = encodeURIComponent(workflowId);
  const url = `${TARGET_PERSONA_BASE}/${campaignId}/target-persona-config/execute/${encodedWorkflowId}/stream`;
  let cancelled = false;
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${userSessionToken}`
        }
      });
      if (!response.ok) {
        const msg = await getErrorMessageFromResponse(
          response,
          `Stream failed: ${response.status}`
        );
        callbacks.onError(msg);
        return;
      }
      reader = response.body?.getReader() ?? null;
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }
      const decoder = new TextDecoder();
      let buffer = '';
      while (!cancelled) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          try {
            const parsed = JSON.parse(data) as TargetPersonaFetchStreamEvent;
            if (parsed?.status) {
              callbacks.onStatus(
                parsed.status as TargetPersonaFetchStatus,
                parsed.error
              );
              if (parsed.status === 'COMPLETED' || parsed.status === 'FAILED') {
                callbacks.onComplete();
                return;
              }
            }
          } catch {
            // ignore non-JSON or unexpected lines
          }
        }
      }
      if (!cancelled) {
        callbacks.onComplete();
      }
    } catch (err) {
      if (!cancelled) {
        callbacks.onError(err instanceof Error ? err.message : 'Stream error');
      }
    }
  })();

  return () => {
    cancelled = true;
    if (reader) {
      reader.cancel().catch(() => {});
    }
  };
}
